import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Grid, Paper, Typography, Box, useTheme, alpha, CircularProgress, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import TrendModal from '../../components/TrendModal';
import dayjs from 'dayjs';
import {
  AlarmOff as NoReminderIcon,
  AssignmentLate as UntouchedIcon,
  CalendarToday as ScheduledIcon,
  CheckCircle as DoneIcon,
  EventAvailable as ConfirmedIcon,
  Group as TotalLeadsIcon,
  Info as DetailsIcon,
  Leaderboard as NewLeadsIcon,
  NotInterested as NotInterestedIcon,
  PersonOff as UnassignedIcon,
  Repeat as MultipleVisitsIcon,
  TrendingUp as ActiveLeadsIcon,
  PendingActions as PendingFollowupIcon,
  Tour as PendingVisitIcon,
} from '@mui/icons-material';

// Define stat meta as an array for easier mapping and fetching
const statMeta = [
  {
    key: 'total_leads',
    title: 'Total Leads',
    icon: <TotalLeadsIcon />,
    color: '#0f172a',
    stat_type: 'total-leads',
    filters: {},
    description: 'All captured leads in the selected period'
  },
  {
    key: 'contacted_leads',
    title: 'Contacted Leads',
    icon: <ActiveLeadsIcon />,
    color: '#10b981',
    stat_type: 'contacted-leads',
    filters: {},
    description: 'Leads with call logs in the period'
  },
  {
    key: 'connected_leads',
    title: 'Connected Leads',
    icon: <ConfirmedIcon />,
    color: '#3b82f6',
    stat_type: 'connected-leads',
    filters: {},
    description: 'Leads with successful call connections'
  },
  {
    key: 'new_followups_scheduled',
    title: 'New Followups Scheduled',
    icon: <PendingFollowupIcon />,
    color: '#8b5cf6',
    stat_type: 'new-followups-scheduled',
    filters: {},
    description: 'Followups scheduled in the date range (excluding no answer)'
  },
  {
    key: 'details_shared',
    title: 'Details Shared',
    icon: <DetailsIcon />,
    color: '#ea580c',
    stat_type: 'details-shared',
    filters: { stage: ['details_shared'] },
    description: 'Project info already shared'
  },
  {
    key: 'site_visit_scheduled',
    title: 'Site Visit Scheduled',
    icon: <ScheduledIcon />,
    color: '#0891b2',
    stat_type: 'site-visit-scheduled',
    filters: { stage: ['site_visit_scheduled'] },
    description: 'Visits with scheduled dates'
  },
  {
    key: 'site_visit_done',
    title: 'Site Visit Done',
    icon: <DoneIcon />,
    color: '#f59e0b',
    stat_type: 'site-visit-done',
    filters: { stage: ['site_visit_done'] },
    description: 'Completed visit outcomes'
  },
  {
    key: 'not_interested_leads',
    title: 'Leads Marked Not Interested',
    icon: <NotInterestedIcon />,
    color: '#dc2626',
    stat_type: 'not-interested-leads',
    filters: { stage: ['not_interested'] },
    description: 'Prospects opted out'
  }
];

const sampleStats = {
  total_leads: { value: 420, trending: 'up', change: { value: '+8%', label: 'vs last week' } },
  contacted_leads: { value: 245, trending: 'up', change: { value: '+12%', label: 'vs last week' } },
  connected_leads: { value: 189, trending: 'up', change: { value: '+10%', label: 'vs last week' } },
  new_followups_scheduled: { value: 56, trending: 'up', change: { value: '+7%', label: 'vs last week' } },
  details_shared: { value: 86, trending: 'up', change: { value: '+9%', label: 'vs last week' } },
  site_visit_scheduled: { value: 44, trending: 'up', change: { value: '+4%', label: 'vs last week' } },
  site_visit_done: { value: 17, trending: 'up', change: { value: '+3%', label: 'vs last week' } },
  not_interested_leads: { value: 12, trending: 'down', change: { value: '-1%', label: 'vs last week' } },
};


const StatCard = ({ title, value, change, icon, color, trending, filters, description, dateRangeState, metaKey, statType, showTrend = true, selectedTeams = [], selectedAgents = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  console.log('key', metaKey);
  
  // Determine trend color and icon
  const getTrendColor = () => {
    if (trending === 'up') return theme.palette.success.main;
    if (trending === 'down') return theme.palette.error.main;
    return theme.palette.grey[500]; // stable
  };

  const getTrendIcon = () => {
    if (trending === 'up') return '↑';
    if (trending === 'down') return '↓';
    return '→'; // stable
  };

  const handleCardClick = () => {
    if (!statType || !dateRangeState?.currentStart || !dateRangeState?.currentEnd) return;
    
    // Build query parameters for leads page
    const params = new URLSearchParams();
    params.set('stat_type', statType);
    params.set('start_date', dateRangeState.currentStart.format('YYYY-MM-DD'));
    params.set('end_date', dateRangeState.currentEnd.format('YYYY-MM-DD'));
    
    // Add filter_id to help with back navigation
    if (dateRangeState.filter?.id) {
      params.set('filter_id', dateRangeState.filter.id);
    }
    
    // Add team filter if provided
    if (selectedTeams && selectedTeams.length > 0) {
      params.set('teams', selectedTeams.join(','));
    }
    
    // Add agent filter if provided
    if (selectedAgents && selectedAgents.length > 0) {
      params.set('agents', selectedAgents.join(','));
    }
    
    // Navigate to leads page
    navigate(`/leads?${params.toString()}`, {
      state: { 
        from: 'dashboard',
        dashboardFilter: dateRangeState.filter?.id,
        dashboardStartDate: dateRangeState.currentStart.format('YYYY-MM-DD'),
        dashboardEndDate: dateRangeState.currentEnd.format('YYYY-MM-DD'),
        selectedTeams: selectedTeams,
        selectedAgents: selectedAgents
      }
    });
  };

  return (
    <Paper
      elevation={0}
      onClick={handleCardClick}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        background: 'background.paper',
        boxShadow: `0 2px 8px ${alpha(color, 0.08)}`,
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(color, 0.15)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: color,
              mb: 0.5,
              fontSize: '1.75rem',
            }}
          >
            {value}
          </Typography>
          {showTrend && change && (
            <Typography
              variant="body2"
              sx={{
                color: getTrendColor(),
                fontSize: '0.75rem',
                mt: 1,
              }}
            >
              <Box component="span" sx={{ mr: 0.5, fontSize: '0.875rem' }}>
                {getTrendIcon()}
              </Box>
              {change.value}
              {change.label && (
                <span style={{ marginLeft: 6, color: theme.palette.text.secondary }}>
                  vs {change.label}
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

const AgentPerformanceStats = ({ dateRangeState, getPeriodType, selectedTeams = [], selectedAgents = [], onViewTrendsRef }) => {
  const theme = useTheme();
  const { fetchLeadPerformance, fetchLeadPerformanceTrend, resetDuplicateCallPrevention } = useDashboard();
  const [stats, setStats] = useState(sampleStats);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef('');
  
  // Trend modal state
  const [isTrendModalOpen, setIsTrendModalOpen] = useState(false);
  const [selectedTrendData, setSelectedTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  
  // Memoize formatted dates to ensure proper dependency tracking
  const startDate = useMemo(() => {
    return dateRangeState?.currentStart?.format('YYYY-MM-DD') || null;
  }, [dateRangeState?.currentStart]);

  const endDate = useMemo(() => {
    return dateRangeState?.currentEnd?.format('YYYY-MM-DD') || null;
  }, [dateRangeState?.currentEnd]);

  const filterId = useMemo(() => {
    return dateRangeState?.filter?.id || '';
  }, [dateRangeState?.filter?.id]);

  // Reset fetch tracking when component mounts
  useEffect(() => {
    // Reset the last fetch ref when component mounts to ensure fresh data
    lastFetchRef.current = '';
    // Reset duplicate call prevention in context
    if (resetDuplicateCallPrevention) {
      resetDuplicateCallPrevention();
    }
  }, []); // Only run on mount

  useEffect(() => {
    if (!startDate || !endDate) {
      console.log('AgentPerformanceStats: Missing date range, not fetching', { startDate, endDate });
      return;
    }
    
    // Create a unique key for this fetch to prevent duplicate calls
    const teamFilterKey = selectedTeams.join(',');
    const agentFilterKey = selectedAgents.join(',');
    const fetchKey = `${startDate}_${endDate}_${filterId}_${teamFilterKey}_${agentFilterKey}`;
    
    // If we're already fetching this exact combination, don't fetch again
    if (fetchKey === lastFetchRef.current) {
      console.log('AgentPerformanceStats: Already fetched this combination, skipping');
      return;
    }
    
    console.log('AgentPerformanceStats: Fetching data with key:', fetchKey);
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchLeadPerformance(startDate, endDate, selectedTeams, selectedAgents);
        
        // Map backend response to frontend expected format
        if (response) {
          const mappedStats = Object.keys(sampleStats).reduce((acc, key) => {
            const sampleStat = sampleStats[key] || { value: '--' };
            const backendStat = response?.[key];

            if (backendStat && typeof backendStat === 'object' && !Array.isArray(backendStat)) {
              acc[key] = {
                ...sampleStat,
                ...backendStat,
                change: backendStat.change
                  ? {
                      ...sampleStat.change,
                      ...backendStat.change,
                    }
                  : sampleStat.change,
              };
            } else if (typeof backendStat === 'number') {
              acc[key] = {
                ...sampleStat,
                value: backendStat,
              };
            } else {
              acc[key] = sampleStat;
            }
            return acc;
          }, {});
          console.log('AgentPerformanceStats: Setting stats:', mappedStats);
          setStats(mappedStats);
        } else {
          console.log('AgentPerformanceStats: No response data, falling back to sample stats');
          setStats(sampleStats);
        }
        lastFetchRef.current = fetchKey;
      } catch (e) {
        console.error('AgentPerformanceStats: Error fetching lead performance:', e);
        setStats(sampleStats);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    filterId,
    selectedTeams,
    selectedAgents,
    fetchLeadPerformance
  ]);

  const handleCloseTrendModal = () => {
    setIsTrendModalOpen(false);
    setSelectedTrendData(null);
  };

  const handleViewAllTrends = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    // Check if date range exceeds 60 days
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const daysDiff = end.diff(start, 'day') + 1;
    
    if (daysDiff > 60) {
      alert('Trend data is only available for date ranges up to 60 days. Please select a shorter date range.');
      return;
    }
    
    setTrendLoading(true);
    setIsTrendModalOpen(true);
    setSelectedTrendData({
      title: 'Lead Performance - Daily Trends',
      data: [],
      metrics: []
    });
    
    try {
      // Fetch all metrics in a single API call
      const allMetricKeys = statMeta.map(meta => meta.key);
      const response = await fetchLeadPerformanceTrend(
        startDate,
        endDate,
        null, // metricType - not used when metric_types is provided
        selectedTeams,
        selectedAgents,
        allMetricKeys // Pass all metric keys
      );
      
      if (response && response.daily_data && response.metric_types) {
        // Format dates for display
        const formattedData = response.daily_data.map(item => ({
          date: dayjs(item.date).format('MMM DD'),
          ...response.metric_types.reduce((acc, metricKey) => {
            acc[metricKey] = item[metricKey] || 0;
            return acc;
          }, {})
        }));
        
        // Map metric keys to their metadata
        const metrics = response.metric_types.map(metricKey => {
          const meta = statMeta.find(m => m.key === metricKey);
          return {
            key: metricKey,
            title: meta?.title || metricKey,
            color: meta?.color || '#000000'
          };
        });
        
        setSelectedTrendData({
          title: 'Lead Performance - Daily Trends',
          data: formattedData,
          metrics: metrics
        });
      }
    } catch (error) {
      console.error('Error fetching trend data:', error);
    } finally {
      setTrendLoading(false);
    }
  }, [startDate, endDate, selectedTeams, selectedAgents, fetchLeadPerformanceTrend, statMeta]);

  // Expose handleViewAllTrends via ref
  useEffect(() => {
    if (onViewTrendsRef) {
      onViewTrendsRef.current = handleViewAllTrends;
    }
  }, [onViewTrendsRef, handleViewAllTrends]);

  // Skeleton UI component for loading state
  const SkeletonCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        background: 'background.paper',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 1 }} />
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Grid container spacing={3}>
        {statMeta.map((meta, i) => (
          <Grid item xs={12} sm={6} md={3} key={meta.key || i}>
            <SkeletonCard />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {statMeta.map((meta) => {
          console.log('Rendering card for meta:', meta);
          const stat = stats[meta.key];
          return (
            <Grid item xs={12} sm={6} md={3} key={meta.key}>
              <StatCard
                title={meta.title}
                value={stat?.value ?? '--'}
                icon={meta.icon}
                color={meta.color}
                trending={stat?.trending}
                change={stat?.change}
                showTrend={meta.showTrend}
                filters={meta.filters}
                description={meta.description}
                dateRangeState={dateRangeState}
                metaKey={meta.key}
                statType={meta.stat_type}
                selectedTeams={selectedTeams}
                selectedAgents={selectedAgents}
              />
            </Grid>
          );
        })}
      </Grid>
      
      {/* Trend Modal */}
      <TrendModal
        open={isTrendModalOpen}
        onClose={handleCloseTrendModal}
        title={selectedTrendData?.title}
        data={trendLoading ? [] : selectedTrendData?.data}
        metrics={selectedTrendData?.metrics}
        chartType="line"
      />
      
      {/* Loading overlay for trend modal */}
      {isTrendModalOpen && trendLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default React.memo(AgentPerformanceStats); 